import clsx from 'clsx';
import { addYears } from 'date-fns/addYears';
import { format } from 'date-fns/format';
import { HTMLAttributes, useEffect, useState } from 'react';

import Close from '@engineering/icons/close';
import {
  BodyBoldText,
  Button,
  datetime,
  String,
  Ui,
} from '@silvertours/front-shared';

import type { DateRange } from './types';
import { DatePicker } from './DatePicker';
import { TimePicker } from './TimePicker';
import { useLocale } from '../Runtime';
import {
  CalendarIcon,
  DatePickerButton,
  DatePickerValue,
  DatePickerValues,
  DateSeparator,
  TopBarHeadline,
} from './DateRangePicker.styles';

const HOUR_IN_MS = 1000 * 60 * 60;

type DatePickerTranslations = {
  close: string;
  confirmButtonText: string;
  pickupTimeText: string;
  pickupTimeLabel: string;
  returnTimeText: string;
  returnTimeLabel: string;
  topBarHeadline: string;
};

type Props = {
  from: Date;
  to: Date;
  open?: boolean;
  vertical?: boolean;
  error?: string;
  onDatesChange?: (dateRange: DateRange) => void;
} & DatePickerTranslations &
  Pick<HTMLAttributes<HTMLDivElement>, 'className'>;

const DateRangePicker = ({
  from,
  to,
  open = false,
  vertical = false,
  error,
  onDatesChange,
  className,
  ...translations
}: Props) => {
  const { language } = useLocale();

  const dateLocale =
    datetime.dateLocales[language as keyof typeof String.dateFormats];
  const localDateFormats =
    String.dateFormats[language as keyof typeof String.dateFormats];

  const [isCalendarVisible, setCalendarVisibility] = useState(open);
  const [selectedDates, setSelectedDates] = useState<DateRange>({
    from,
    to,
  });

  useEffect(() => {
    setSelectedDates({ from, to });
  }, [from, to]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setCalendarVisibility(false);
  };

  const handleOnDatesChange = (dateRange: DateRange) => {
    let newDateRange = { from: new Date(), to: new Date() };
    if (
      dateRange.from &&
      dateRange.to &&
      selectedDates.from &&
      selectedDates.to
    ) {
      newDateRange = {
        from: datetime.addTimeToDate(
          dateRange.from,
          selectedDates.from.getHours(),
          selectedDates.from.getMinutes(),
        ),
        to: datetime.addTimeToDate(
          dateRange.to,
          selectedDates.to.getHours(),
          selectedDates.to!.getMinutes(),
        ),
      };
      if (
        format(dateRange.from, String.isoDateTime).split('T')[0] ===
        format(dateRange.to, String.isoDateTime).split('T')[0]
      ) {
        newDateRange = {
          from: datetime.addTimeToDate(dateRange.from, 10, 0),
          to: datetime.addTimeToDate(dateRange.to, 17, 0),
        };
      }
      if (typeof onDatesChange !== 'undefined') {
        onDatesChange(newDateRange);
      }
      setSelectedDates(newDateRange);
    }
  };

  const handleOnTimeChange = (time: string, type: 'from' | 'to') => {
    const [hours, minutes] = time.split(':');
    if (selectedDates[type]) {
      const dateWithTime = datetime.addTimeToDate(
        selectedDates[type]!,
        Number(hours),
        Number(minutes),
      );

      const newDateRange = { ...selectedDates, [type]: dateWithTime };
      if (typeof onDatesChange !== 'undefined') {
        onDatesChange(newDateRange);
      }
      setSelectedDates(newDateRange);
    }
  };

  const getDays = () => {
    if (selectedDates.from && selectedDates.to) {
      // Inspired by https://youmightnotneed.com/date-fns#differenceInDays,
      // because differenceInHours() from date-fns always rounds which is not what we want

      const days =
        ((selectedDates.to as any) - (selectedDates.from as any)) /
        HOUR_IN_MS /
        24;
      return Number.isInteger(days) ? days : Math.ceil(days);
    }
    return 1;
  };

  // calendar range: current month up to two years
  const now = new Date();
  const calendarRange = {
    start: now,
    end: addYears(now, 2),
  };

  // scrolling shadow effect
  const shadowEffect = vertical
    ? {
        background: `
      linear-gradient(white 30%, rgba(255, 255, 255, 0)),
      linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
      radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)),
      radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)) 0 100%`,
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'white',
        backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
        backgroundAttachment: 'local, local, scroll, scroll',
      }
    : undefined;

  const renderDate = (date: Date | null) =>
    date &&
    format(
      date,
      vertical ? localDateFormats.dayTimeShort : localDateFormats.dayTimeLong,
      {
        locale: dateLocale,
      },
    );

  return (
    <div className={clsx('relative', className)}>
      {isCalendarVisible && (
        // OnKeyDown is necessary for accessibility reasons
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div
          className="fixed bottom-0 left-0 right-0 top-0 select-none bg-transparent"
          onClick={handleBackdropClick}
          data-testid="date-picker-backdrop"
        />
      )}
      <DatePickerButton
        type="button"
        id="datepicker"
        onClick={() => setCalendarVisibility(true)}
        isInvalid={!!error}
      >
        <CalendarIcon id="datepicker-cal-icon" />
        <DatePickerValues id="datepickerValue">
          <DatePickerValue>{renderDate(selectedDates.from)}</DatePickerValue>
          <DateSeparator />
          <DatePickerValue>{renderDate(selectedDates.to)}</DatePickerValue>
        </DatePickerValues>
      </DatePickerButton>
      {error && <Ui.ErrorMessage>{error}</Ui.ErrorMessage>}
      {isCalendarVisible && (
        <div className="md:border-color8 fixed bottom-0 left-0 right-0 top-0 z-50 flex flex-col rounded-md bg-white md:absolute md:bottom-auto md:right-auto md:top-auto md:mt-10 md:block md:w-[688px] md:overflow-hidden md:border-2">
          {vertical && (
            <div className="flex flex-row items-center justify-between bg-white p-40">
              <TopBarHeadline>{translations.topBarHeadline}</TopBarHeadline>
              <button
                type="button"
                aria-label={translations.close}
                onClick={() => setCalendarVisibility(false)}
              >
                <Close className="fill-primary h-60 w-60" />
              </button>
            </div>
          )}
          <div
            className="w-auto flex-grow overflow-y-scroll md:overflow-hidden"
            style={shadowEffect}
            data-testid="date-picker"
          >
            <DatePicker
              onDatesChange={handleOnDatesChange}
              disableNavigation={vertical}
              disabled={{ before: new Date() }}
              numberOfMonths={vertical ? 15 : 2}
              fromMonth={calendarRange.start}
              toMonth={calendarRange.end}
              from={selectedDates.from ? selectedDates.from : undefined}
              to={selectedDates.to ? selectedDates.to : undefined}
              locale={dateLocale}
              defaultMonth={
                (vertical ? undefined : selectedDates.from) || undefined
              }
              scrollToSelectedMonth={vertical}
            />
          </div>
          <div className="bg-color5 md:p-50 flex flex-col items-center justify-between gap-40 p-40 md:flex-row">
            <div className="flex justify-between gap-60 md:justify-start md:gap-40">
              <TimePicker
                type="from"
                onChange={handleOnTimeChange}
                returnTimeLabel={translations.returnTimeLabel}
                returnTimeText={translations.returnTimeText}
                pickupTimeLabel={translations.pickupTimeLabel}
                pickupTimeText={translations.pickupTimeText}
                value={
                  selectedDates.from
                    ? format(selectedDates.from, localDateFormats.time)
                    : undefined
                }
              />
              <TimePicker
                type="to"
                onChange={handleOnTimeChange}
                returnTimeLabel={translations.returnTimeLabel}
                returnTimeText={translations.returnTimeText}
                pickupTimeLabel={translations.pickupTimeLabel}
                pickupTimeText={translations.pickupTimeText}
                value={
                  selectedDates.to
                    ? format(selectedDates.to, localDateFormats.time)
                    : undefined
                }
              />
            </div>
            <Button
              className="w-full md:w-auto"
              size="small"
              type="button"
              variant="primary"
              onClick={() => setCalendarVisibility(false)}
            >
              <BodyBoldText inline textColor="white">
                {translations.confirmButtonText?.replace(
                  '{{days}}',
                  getDays().toString(),
                )}
              </BodyBoldText>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export { DateRangePicker };
